import { useQuery, useQueryClient } from '@tanstack/react-query';
import { recommendationService } from '@services/recommendationService';
import { useState, useCallback, useMemo } from 'react';

const STALE_TIME = 5 * 60 * 1000;

export const useRecommendations = () => {
  const queryClient = useQueryClient();

  // Estágio 1: regras heurísticas (rápido, ~500ms)
  const {
    data: quickData,
    isLoading: isLoadingQuick,
    refetch: refetchQuick,
  } = useQuery({
    queryKey: ['recommendations', 'quick'],
    queryFn: recommendationService.getInsightsQuick,
    staleTime: STALE_TIME,
  });

  // Estágio 2: regras + IA Anthropic (lento, ~5-15s)
  const {
    data: fullData,
    isLoading: isLoadingFull,
    refetch: refetchFull,
  } = useQuery({
    queryKey: ['recommendations', 'full'],
    queryFn: recommendationService.getInsights,
    staleTime: STALE_TIME,
  });

  // Exibe as regras imediatamente; substitui pelos dados completos quando prontos
  const recommendations = useMemo(() => fullData || quickData || [], [fullData, quickData]);

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['recommendations'] });
    refetchQuick();
    refetchFull();
  };

  return {
    recommendations,
    // Tela em branco total apenas antes das regras chegarem
    isLoading: isLoadingQuick,
    // Ainda carregando a camada de IA (regras já visíveis)
    isLoadingAI: !isLoadingQuick && isLoadingFull,
    // Dados de IA chegaram e podem ter novos cards
    hasAIInsights: !!fullData && fullData.some(r => r.source === 'ai'),
    error: null,
    refetch: refresh,
  };
};

export const useAdvisorChat = () => {
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const sendMessage = useCallback(async (content) => {
    const userMsg = { role: 'user', content };
    const updated = [...messages, userMsg];
    setMessages(updated);
    setIsLoading(true);

    try {
      const reply = await recommendationService.chat(updated);
      setMessages(prev => [...prev, reply]);
    } catch (err) {
      setMessages(prev => [
        ...prev,
        { role: 'assistant', content: 'Ocorreu um erro. Tente novamente.' },
      ]);
    } finally {
      setIsLoading(false);
    }
  }, [messages]);

  const clearChat = useCallback(() => setMessages([]), []);

  return { messages, sendMessage, clearChat, isLoading };
};

export default useRecommendations;
